/* eslint-disable jsx-a11y/anchor-is-valid */
import { parse } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import React, { useCallback, useReducer, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
	Button,
	Checkbox,
	Divider,
	Grid,
	Header,
	Icon,
	List,
	Message,
	Modal,
	Pagination,
	Popup,
	Segment,
	Select,
	Table,
} from "semantic-ui-react";
import uuid from "uuid/v4";

import { useLocation, useHistory } from "react-router-dom";
import { Config } from "../../../config/api";
import { LangContext } from "../../../context";
import {
	autoSortFn,
	currencySymbol,
	downloadBlob,
	formatMoney,
	getApiDate,
	getCampaignTypeBasedOnLD,
	isNil,
} from "../../../libs/common_utils";
import { onBannerDismiss } from "../../../libs/component_utils";
import Registry from "../../../libs/register_storage";
import Pager from "../../../models/pager";
import CommonService from "../../../services/common";
import ReportService from "../../../services/reports";
import StrategiesService from "../../../services/strategy";
import { graph_metrics } from "../../campaign/fixtures";
import ChartDiv from "../../common/chart";
import GridEmptyRow from "../../common/gridEmptyRow";
import StrategyGridItem from "../common/strategy_grid_item";
import StrategyGridContext from "./context";
import FilterControl from "./filter_control";
import Filter from "./models/filter";
import { strategiesActions as sA, strategiesGridReducer } from "./reducers";
import { StrategiesBulkEditModal } from "../edit/strategies_bulk_edit_modal";

const initialState = {
	strategies: [],
	campaign: {},
	pager: new Pager(),
	filter: new Filter(),
};

const default_metrics = {
	METRIC_1: "total_spend",
	METRIC_2: "impressions",
};
const storage = new Registry(window.sessionStorage);
const backUrl = "/campaigns";

export const StrategiesPage = ({ match, history }) => {
	const campaign_id = match.params.campaign_id;
	const routeHistory = useHistory();
	const location = useLocation();

	const intl = useIntl();
	const [state, dispatch] = useReducer(strategiesGridReducer, initialState);
	const [commonData, setCommonData] = useState({
		currencies: [],
	});
	const [sortBy, setSortBy] = useState(null);
	const [sortDir, setSortDir] = useState('asc');
	const allSpendRowsRef = useRef([]);
	const allPerformanceRowsRef = useRef([]);

	const [graph, setGraph] = React.useState({
		data: [],
		loading: false,
	});
	const [graph_filter, setGraphFilter] = React.useState({
		metric_1: default_metrics.METRIC_1,
		metric_2: default_metrics.METRIC_2,
	});

	const [gridLoading, setGridLoading] = useState(true);
	const [strategySelected, setStrategySelected] = React.useState({});
	const [strategyForDuplication, setStrategyForDuplication] =
		React.useState(null);
	const [duplicatedStrategies, setDuplicatedStrategies] = React.useState([]);
	const [duplicatedStrategiesFailures, setDuplicatedStrategiesFailures] =
		React.useState([]);
  const [selectedStrategies, setSelectedStrategies] = React.useState([]);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = React.useState(false);
	const timestamp = React.useRef(0);
	const showSuccessMessage = !isNil(history.location.state);
  const [bulkEditResponseMessage, setBulkEditResponseMessage] = useState({successMessage: [], errorMessage: []});
	const [isDownloading, setIsDownloading] = useState(false);
  const [showBulkEditResponseMessage, setShowBulkEditResponseMessage] = useState(false);
	const [performanceGridData, setPerformanceGridData] = React.useState(
		storage.getItem("strategies_performance_grid")
	);
	const [spendGridData, setSpendGridData] = React.useState(
		storage.getItem("strategies_spend_grid")
	);
	const isPerformanceView = state.filter.isPerformanceView();
	const selectedGridData = isPerformanceView ? performanceGridData: spendGridData;
	const isCampaignWasSaved =
		!isNil(history.location.state) &&
		history.location.state.type === "campaign";

	const services = React.useRef(
		new Map([
			["strategies", new StrategiesService()],
			["common", new CommonService()],
			["report", new ReportService()],
		])
	);
	const campaign_type = React.useRef("");
	const {
		whitelabelOfflineAttribution,
		whitelabelSupplyPmpFlag,
		whitelabelSupplyAudienceFlag,
		whitelabelSupplyOpenFlag,
	} = useFlags();
	const lang_context = React.useContext(LangContext);

	const isStrategySelected = () => Object.keys(strategySelected).length > 0;

	/**
	 * initial load strategies
	 */
	React.useEffect(() => {
		state.filter.reset();
		state.pager.reset();

		let active = true;
		(async () => {
			const [, currencies] = await Promise.all([
				getStrategies(state.filter, state.pager, true),
				services.current.get("common").getCurrencies(),
			]);

			if (!active) return;
			setCommonData({
				...commonData,
				currencies: currencies.data,
			});
		})();

		// clear cache
		return () => {
			active = false;
			state.filter.reset();
			state.pager.reset();
		};
	}, [campaign_id]);

  /**
   * only re-store performance column if flag is on
   */
  useEffect(() => {
    const defaultVisibleColumns = performanceColumns(
      whitelabelOfflineAttribution
    );
    let currentStoredColumns =
      storage.getItem("strategies_performance_grid") || {};
    if (!whitelabelOfflineAttribution) {
      const keysToRemove = Object.keys(offlineAttributionColumns);
      currentStoredColumns = Object.fromEntries(
        Object.entries(currentStoredColumns).filter(
          ([key]) => !keysToRemove.includes(key)
        )
      );
    }
    // merge default columns with stored columns to maintain user preferences
    const resolvedColumns = {
      ...defaultVisibleColumns,
      ...currentStoredColumns,
    };

    storage.setItem("strategies_performance_grid", resolvedColumns);
    setPerformanceGridData(resolvedColumns);
  }, [whitelabelOfflineAttribution]);

	/**
	 * Storing grid information data
	 */
	React.useEffect(() => {
		storage.setItem("strategies_performance_grid", performanceGridData);
		storage.setItem("strategies_spend_grid", spendGridData);
	}, [performanceGridData, spendGridData]);

	/**
	 * Update spendGridData to exclude strategy_pacing_ratio if campaign type is PG
	 */
	React.useEffect(() => {
		if (campaign_type.current === "Programmatic Guaranteed" && spendGridData?.strategy_pacing_ratio !== undefined) {
			const { strategy_pacing_ratio, ...spendGridDataWithoutPacingRatio } = spendGridData;
			setSpendGridData(spendGridDataWithoutPacingRatio);
		}
		else if (spendGridData && !spendGridData.hasOwnProperty('strategy_pacing_ratio')) {
			const { strategy_min_bid, strategy_max_bid, ...rest } = spendGridData;
			setSpendGridData({
				...rest,
				strategy_pacing_ratio: true,
				strategy_min_bid,
				strategy_max_bid,
			});
		}
	}, [campaign_type.current, state.campaign.type]);

	/**
	 * get graph data for strategy being selected
	 */
	React.useEffect(() => {
		if (!strategySelected.id) {
			return undefined;
		}

		window.scrollTo(0, 0);
		let active = true;
		(async () => {
			try {
				setGraph({
					data: [],
					loading: true,
				});
				const params = Object.assign(
					{
						id: strategySelected.id,
						m1: graph_filter.metric_1,
						m2: graph_filter.metric_2,
					},
					state.filter.toGraphJson()
				);

				const r = await services.current.get("report").strategyGraph(params);
				if (!active) return;
				/** @namespace {object} r.data **/
				let data2display = r.data.graph_data.map((x) => ({
					...x,
					date: parse(x.date, "yyyy-MM-dd", new Date()).valueOf(),
				}));
				data2display = data2display.sort((a, b) => a.date - b.date);
				timestamp.current = Date.now();
				setGraph({
					...graph,
					data: data2display,
					loading: false,
				});
			} catch (e) {
				if (!active) return;
				console.error(e);
				setGraph({
					...graph,
					loading: false,
				});
			}
		})();
		return () => { active = false; };
	}, [strategySelected, graph_filter, state.filter.start_date]);

	const sortStrategies = useCallback((sort_by, order) => {
		console.log(sort_by, order);
	}, []);

	/**
	 * open chart
	 */
	const openChartAccordion = () => {
		document.getElementById("chart_swap_id").setAttribute("open", "true");
	};

	/**
	 * get page
	 * @param e
	 * @param activePage
	 */
	const getPage = (e, { activePage }) => {
		state.pager.setPage(activePage);
		getStrategies(state.filter, state.pager).then(() => console.log);
	};

	/**
	 * load strategies from API
	 * @param {object} filter
	 * @param {object} pager
	 * @param {boolean} initial_load
	 * @return {Promise<void>}
	 */
	const getStrategies = async (filter, pager, initial_load = false) => {
		const strategies_service = services.current.get("strategies");
		try {
			setGridLoading(true);

			let params = pager.toJsonNoLimit();
			if (filter) {
				params = Object.assign(params, filter.toRequestJson());
			}

			const r = await strategies_service.list(campaign_id, params);
			let meta = r.meta;
			meta.page = pager.page;
			if (isPerformanceView) {
				allPerformanceRowsRef.current = r.data.strategies;
			} else {
				allSpendRowsRef.current = r.data.strategies;
			}

			if (initial_load && Boolean(r.data.strategies.length)) {
				setStrategySelected(r.data.strategies[0]);
			}

			if (r.data["type"].toLowerCase() === "pg") {
				campaign_type.current = "Programmatic Guaranteed";
			} else {
				const non_pg_type = getCampaignTypeBasedOnLD(
					whitelabelSupplyPmpFlag,
					whitelabelSupplyAudienceFlag,
					whitelabelSupplyOpenFlag,
					lang_context
				);
				campaign_type.current = non_pg_type || r.data["type"];
			}

			dispatch({ type: sA.INIT, data: r.data, pager: meta });
		} catch (e) {
			// ignore error
			console.log(e);
		} finally {
			setGridLoading(false);
		}
	};

	const duplicateStrategy = async (item) => {
		const strategies_service = services.current.get("strategies");
		strategies_service
			.duplicate(item["id"])
			.then(() => {
				setDuplicatedStrategies([...duplicatedStrategies, item]);
				getStrategies(state.filter, state.pager);
			})
			.catch((e) => {
				setDuplicatedStrategiesFailures([...duplicatedStrategies, {...item, errorMessage: e.error?.message ? e.error?.message : "" }]);
			});
	};

	/**
	 * filter strategies
	 * @param {object} json
	 */
	const filterStrategies = (json) => {
		state.pager.reset();
		state.filter.fromJson(json);

		setSortBy(null);
		setSortDir('asc');

		// server paginated fetch
		const params = { ...state.pager.toJson(), ...state.filter.toRequestJson?.() };
		const strategies_service = services.current.get("strategies");
		// Perform filter check here because this function runs before the page recognizes the `view_by` change
		const isPerformance = params.view_by === "performance";

		(async () => {
			try {
				setGridLoading(true);
				const response = await strategies_service.list(campaign_id, params);
				if (isPerformance) {
					allPerformanceRowsRef.current = response.data.strategies;
				} else {
					allSpendRowsRef.current = response.data.strategies;
				}
				dispatch({ type: sA.INIT, data: response.data, pager: response.meta });
			} catch (error) {
				console.error(error);
			} finally {
				setGridLoading(false);
			}
		})();
	};

	/**
	 * navigate user to create campaign page
	 */
	const navigateToCreatePage = () => {
		history.push(`/campaign/${campaign_id}/strategies/create`);
	};

	/**
	 * navigate user to edit page
	 * @param {number} strategy_id
	 */
	const navigateToEditPage = (strategy_id) => {
		history.push(`/campaign/${campaign_id}/strategies/edit/${strategy_id}`, {});
	};

	const navigateBack = () => {
		history.push(backUrl);
	}

	/**
	 * get edit page URL by strategy id
	 */
	const getEditPageHref = useCallback(
		(strategy_id) => `./strategies/edit/${strategy_id}`,
		[]
	);

	/**
	 * Update filter metrics
	 * @param e
	 * @param {string} name
	 * @param {string} value
	 */
	const onChangeMetric = (e, { name, value }) => {
		setGraphFilter({
			...graph_filter,
			[name]: value,
		});
	};

  useEffect(() => {
    setShowBulkEditResponseMessage(bulkEditResponseMessage['successMessage'].length > 0 || bulkEditResponseMessage['errorMessage'].length > 0);
    const timer = setTimeout(handleBannerDismiss, 10000);
    return () => clearTimeout(timer);
  }, [bulkEditResponseMessage]);

  const handleBannerDismiss = useCallback(() => {
    setBulkEditResponseMessage({ successMessage: [], errorMessage: [] });
  }, [setBulkEditResponseMessage]);

	const applySortAndShowPage = (page = 1, columnKey, direction) => {
		const list = (isPerformanceView ? allPerformanceRowsRef : allSpendRowsRef).current.slice().sort(autoSortFn(columnKey, direction));
		const perPage = state.pager.limit || 20;
		const start = (page - 1) * perPage;
		const data = list.slice(start, start + perPage);

		dispatch({ type: sA.UPDATE, data });
	};


	const onHeaderSort = async (columnKey) => {
		const nextDir = columnKey === sortBy ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';

		// turn page to 1 for new sort
		state.pager.setPage(1);

		// fetch-all once for current filter if not cached
		if ((isPerformanceView && !allPerformanceRowsRef.current.length) || (!isPerformanceView && !allSpendRowsRef.current.length)) {
			const params = { ...state.pager.toJsonNoLimit(), ...state.filter.toRequestJson?.() };

			const svc = services.current.get('strategies');
			const resp = await svc.list(campaign_id, params);
			if (isPerformanceView) {
				allPerformanceRowsRef.current = resp.data.strategies || [];
			} else {
				allSpendRowsRef.current = resp.data.strategies || [];
			}
			let meta = resp.meta;
			meta.page = state.pager.page;
			dispatch({ type: sA.INIT, data: resp.data, pager: meta });
		}

		// now paginate client-side on the full list
		applySortAndShowPage(1, columnKey, nextDir);

		// update state AFTER applying (for header arrow + future clicks)
		setSortBy(columnKey);
		setSortDir(nextDir);
	};
	
	const handleStrategiesDownload = async() => {
		const activeColumns = Object.keys(selectedGridData).filter((key) => selectedGridData[key]);
		try {
			setIsDownloading(true);
			const response = await services.current.get('strategies').download(campaign_id, {
				output: "csv",
				columns: activeColumns.join(","),
				...state.filter.toRequestJson?.(),
				// Must add for strategy download
				page_limit: 100,
				page_offset: 0
			});
			const blob = new Blob([response], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});
			
			downloadBlob(blob, "strategies.xlsx")
		} catch (e) {
			console.error(e);
		} finally {
			setIsDownloading(false);
		}
	}

	return (
		<Segment loading={gridLoading} basic style={{ padding: "0" }}>
			<StrategyGridContext.Provider
				value={{
					sorting: sortStrategies,
					campaign: state.campaign,
					getPage,
					filtering: filterStrategies,
					navigateToCreatePage,
					navigateToEditPage,
					getEditPageHref,
					currency: currencySymbol(
						state.campaign.currency_code,
						commonData.currencies
					),
					setStrategySelected,
					openChartAccordion,
					strategyForDuplication,
					setStrategyForDuplication,
					duplicateStrategy,
          selectedStrategies,
          setSelectedStrategies,
          isBulkEditModalOpen,
          setIsBulkEditModalOpen,
          dispatch,
          setBulkEditResponseMessage,
					onHeaderSort,
					sortBy,
					sortDir,
					setPerformanceGridData,
					setSpendGridData,
				}}
			>
				{showSuccessMessage &&
					(isCampaignWasSaved ? (
						<Message
							success
							className="page-success-message"
							attached
							onDismiss={onBannerDismiss}
						>
							{intl.formatMessage({
								id: "MESSAGE_CAMPAIGN_CREATED",
								defaultMessage: "Campaign successfully created",
							})}
						</Message>
					) : (
						<StrategiesSuccessMessage details={history.location.state || {}} />
					))}
          {showBulkEditResponseMessage &&
            <>
              <Message
                  success
                  hidden={bulkEditResponseMessage['successMessage'].length === 0}
                  onDismiss={handleBannerDismiss}>
                    <div>
                      {intl.formatMessage({
                        id: "MESSAGE_STRATEGY_BULK_EDIT_UPDATED",
                        defaultMessage: "Strategies successfully updated"
                      })}
                      <ul>
                        {bulkEditResponseMessage['successMessage'].map((strategy) => (
                          <li key={strategy.id}>{strategy.name}</li>
                        ))}
                      </ul>
                    </div>
                </Message>
                <Message
                  error
                  hidden={bulkEditResponseMessage['errorMessage'].length === 0}
                  onDismiss={handleBannerDismiss}>
                  <div>
                    {intl.formatMessage({
                      id: "MESSAGE_STRATEGY_BULK_EDIT_NOT_UPDATED",
                      defaultMessage: "These strategies were not updated"
                    })}
                    <ul>
                      {bulkEditResponseMessage['errorMessage'].map((strategy) => (
                        <li key={uuid()}>{strategy.message}</li>
                      ))}
                    </ul>
                  </div>
                </Message>
            </>
          }
				{duplicatedStrategies.length > 0 && (
					<Message success onDismiss={() => setDuplicatedStrategies([])}>
						{intl.formatMessage(
							{
								id: "SUCCESSFUL_DUPLICATION_MSG",
								defaultMessage: `Successfully duplicated: ${duplicatedStrategies
									.map((item) => item.title)
									.join(", ")}`,
							},
							{
								title: duplicatedStrategies
									.map((item) => item.title)
									.join(", "),
							}
						)}
					</Message>
				)}
				{duplicatedStrategiesFailures.length > 0 && (
					<Message
						negative
						onDismiss={() => setDuplicatedStrategiesFailures([])}
					>
						{intl.formatMessage(
							{
								id: "FAILED_DUPLICATION_MSG",
								defaultMessage: `Failed duplicating: ${duplicatedStrategiesFailures
									.map((item) => item.title)
									.join(", ")}`,
							},
							{
								title: duplicatedStrategiesFailures
									.map((item) => item.title)
									.join(", "),
							}
						)}
						{duplicatedStrategiesFailures.map((item, index) => <span key={`message-${index}`} className="error-message-child">{`${intl.formatMessage(
							{
								id: "ERROR",
								defaultMessage: `Error`,
							}
						)}: ${item.errorMessage}`}</span>)}
					</Message>
				)}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
					<div style={{ display: "flex", alignItems: "center", fontSize: '1.75rem', gap: '0.5rem' }}>
						<Button
							onClick={navigateBack}
							icon
							title={intl.formatMessage({
								id: "HINT_BACK_CAMPAIGNS",
								defaultMessage: "Back to the Campaign grid",
							})}
							style={{color: "#000", background: "none", padding: "10px"}}
						>
							<Icon name="arrow left" size="small" style={{fontSize: '21px'}} />
						</Button>
						
						<h1 style={{marginTop: '0'}}>
							{intl.formatMessage({
								id: "HEADING_STRATEGIES",
								defaultMessage: "Strategies",
							})}
						</h1>
					</div>
					<Button compact type="button" onClick={handleStrategiesDownload} loading={isDownloading}>
						{intl.formatMessage({
							id: "BTN_DOWNLOAD_STRATEGIES",
							defaultMessage: "Download Strategies",
						})}
					</Button>
				</div>
				<Divider hidden />
				<StrategyCampaignInfo
					item={state.campaign}
					currencies={commonData.currencies}
					campaign_type={campaign_type.current}
				/>
				<Divider hidden />
				{isStrategySelected() && (
					<details id="chart_swap_id">
						<summary>
							<hr />{" "}
							<div>
								{intl.formatMessage({
									id: "BUTTON_SHOW_HIDE_CHART",
									defaultMessage: "Show / hide chart",
								})}
							</div>
						</summary>
						<Grid>
							<Grid.Column verticalAlign="middle" style={{ width: "auto" }}>
								<strong>{strategySelected.title}</strong>
							</Grid.Column>
							<Grid.Column width={2}>
								<Select
									selection
									fluid
									options={graph_metrics(intl).filter(
										(x) => x.value !== graph_filter.metric_2
									)}
									name="metric_1"
									disabled={graph.loading}
									onChange={onChangeMetric}
									value={graph_filter.metric_1}
								/>
							</Grid.Column>
							<Grid.Column
								style={{ width: "15px" }}
								verticalAlign="middle"
								textAlign="center"
							>
								{intl.formatMessage({
									id: "LABEL_CAMPAIGN_VS_METRIC",
									defaultMessage: "vs",
								})}
							</Grid.Column>
							<Grid.Column width={2}>
								<Select
									selection
									fluid
									options={graph_metrics(intl).filter(
										(x) => x.value !== graph_filter.metric_1
									)}
									name="metric_2"
									disabled={graph.loading}
									onChange={onChangeMetric}
									value={graph_filter.metric_2}
								/>
							</Grid.Column>
						</Grid>
						{graph.loading ? (
							<Segment loading />
						) : (
							<ChartDiv
								updated={timestamp.current}
								filter={graph_filter}
								data={graph.data}
							/>
						)}
					</details>
				)}
				<Divider hidden />
				<StrategiesGrid
					items={state.strategies}
					loading={gridLoading}
					controls={{ pager: state.pager, filter: state.filter }}
					selectedGridData={selectedGridData}
					campaign_type={campaign_type.current}
				/>
			</StrategyGridContext.Provider>
		</Segment>
	);
};

/**
 * show campaign info
 * @param item
 * @param currencies
 * @param {string} campaign_type
 * @return {*}
 * @constructor
 */
const StrategyCampaignInfo = ({ item, currencies, campaign_type }) => {
	const intl = useIntl();

	if (!Object.keys(item).length) {
		return <></>;
	}

	return (
		<Grid columns="4" className="campaign_info">
			<Grid.Column>
				<label>
					{intl.formatMessage({
						id: "LABEL_CAMPAIGN",
						defaultMessage: "Campaign",
					})}
				</label>
				<Popup
					size="tiny"
					content={item.title}
					inverted
					trigger={
						<Header as="h4" className="restrict_length">
							{item.title}
						</Header>
					}
				/>
			</Grid.Column>
			<Grid.Column>
				<label>
					{intl.formatMessage({
						id: "LABEL_CAMPAIGN_TYPE",
						defaultMessage: "Campaign Type",
					})}
				</label>
				<Header as="h4">{campaign_type}</Header>
			</Grid.Column>
			<Grid.Column>
				<label>
					{intl.formatMessage({
						id: "LABEL_CAMPAIGN_BUDGET",
						defaultMessage: "Campaign Budget",
					})}
				</label>
				<Header as="h4">
					{formatMoney(item.budget, item.currency_code, Config.defaultLocale, 2)}
				</Header>
			</Grid.Column>
			<Grid.Column>
				<label>
					{intl.formatMessage({
						id: "LABEL_CAMPAIGN_START_END",
						defaultMessage: "Campaign Start - End",
					})}
				</label>
				<Header as="h4">
					{getApiDate(item.start_date, Config.dateTimeFormat)} /
					{getApiDate(item.end_date, Config.dateTimeFormat)}
				</Header>
			</Grid.Column>
		</Grid>
	);
};
StrategyCampaignInfo.propTypes = {
	item: PropTypes.object.isRequired,
	campaign_type: PropTypes.string.isRequired,
};

/**
 * Generate success message
 * @param {object} details
 * @return {*}
 * @constructor
 */
const StrategiesSuccessMessage = ({ details }) => {
	const intl = useIntl();

	let timer;
	React.useLayoutEffect(() => {
		timer = setTimeout(onBannerDismiss, 10000);
		return () => {
			clearTimeout(timer);
		};
	}, []);

	if (details.action === "created") {
		return (
			<Message
				success
				className="page-success-message"
				attached
				onDismiss={onBannerDismiss}
			>
				{intl.formatMessage({
					id: "MESSAGE_STRATEGY_CREATED",
					defaultMessage: "Strategy successfully created",
				})}
			</Message>
		);
	} else if (details.action === "updated") {
		return (
			<Message
				success
				className="page-success-message"
				attached
				onDismiss={onBannerDismiss}
			>
				{intl.formatMessage(
					{
						id: "MESSAGE_STRATEGY_UPDATED",
						defaultMessage: "Strategy {name} updated",
					},
					{
						name: details.name,
					}
				)}
			</Message>
		);
	}

	return <></>;
};
StrategiesSuccessMessage.propTypes = {
	details: PropTypes.object.isRequired,
};

	const columnNames = (intl, campaign_type = "") => {
		const allColumns = [
			// Performance columns
			["id", intl.formatMessage({
				id: "LABEL_STRATEGY_ID",
				defaultMessage: "Strategy ID",
			})],
			["title", intl.formatMessage({
				id: "LABEL_STRATEGY_NAME",
				defaultMessage: "Strategy Name",
			})],
			["status", intl.formatMessage({
				id: "LABEL_STATUS",
				defaultMessage: "Status",
			})],
			["start_date", intl.formatMessage({
				id: "LABEL_START_DATE",
				defaultMessage: "Start Date",
			})],
			["end_date", intl.formatMessage({
				id: "LABEL_END_DATE",
				defaultMessage: "End Date",
			})],
			["impressions", intl.formatMessage({
				id: "LABEL_IMPRESSIONS",
				defaultMessage: "Impressions",
			})],
			["clicks", intl.formatMessage({
				id: "LABEL_CLICKS",
				defaultMessage: "Clicks",
			})],
			["total_conversions", intl.formatMessage({
				id: "LABEL_TOTAL_CONVERSIONS",
				defaultMessage: "Total Conversions",
			})],
			["total_revenue", intl.formatMessage({
				id: "LABEL_TOTAL_REVENUE",
				defaultMessage: "Total Revenue",
			})],
			["spend_to_date", intl.formatMessage({
				id: "LABEL_TOTAL_SPEND",
				defaultMessage: "Total Spend",
			})],
			["total_spend_cpa", intl.formatMessage({
				id: "LABEL_TOTAL_SPEND_CPA",
				defaultMessage: "Total Spend eCPA",
			})],
			["total_spend_cpc", intl.formatMessage({
				id: "LABEL_TOTAL_SPEND_CPC",
				defaultMessage: "Total Spend eCPC",
			})],
			["total_spend_cpm", intl.formatMessage({
				id: "LABEL_TOTAL_SPEND_CPM",
				defaultMessage: "Total Spend eCPM",
			})],
			["ctr", intl.formatMessage({
				id: "LABEL_CTR",
				defaultMessage: "CTR",
			})],
			["video_complete", intl.formatMessage({
				id: "LABEL_VIDEO_COMPLETE_RATE",
				defaultMessage: "100% Completed Rate",
			})],
			["total_spend_roi", intl.formatMessage({
				id: "LABEL_TOTAL_ROI",
				defaultMessage: "Total ROI",
			})],
			["online_conversions", intl.formatMessage({
				id: "LABEL_ONLINE_CONVERSIONS",
				defaultMessage: "Online Conversions",
			})],
			["online_revenue", intl.formatMessage({
				id: "LABEL_ONLINE_REVENUE",
				defaultMessage: "Online Revenue",
			})],
			["online_roi", intl.formatMessage({
				id: "LABEL_ONLINE_ROI",
				defaultMessage: "Online ROI",
			})],
			["offline_conversions", intl.formatMessage({
				id: "LABEL_OFFLINE_CONVERSIONS",
				defaultMessage: "Offline Conversions",
			})],
			["offline_revenue", intl.formatMessage({
				id: "LABEL_OFFLINE_REVENUE",
				defaultMessage: "Offline Revenue",
			})],
			["offline_roi", intl.formatMessage({
				id: "LABEL_OFFLINE_ROI",
				defaultMessage: "Offline ROI",
			})],
			["strategy_pacing_type", intl.formatMessage({
				id: "LABEL_PACING_TYPE",
				defaultMessage: "Pacing Type",
			})],
			["total_spend_yesterday", intl.formatMessage({
				id: "LABEL_SPEND_YESTERDAY",
				defaultMessage: "Spend Yesterday",
			})],
			["strategy_pacing_ratio", intl.formatMessage({
				id: "LABEL_PACING_RATIO",
				defaultMessage: "Pacing Ratio",
			})],
			["strategy_max_bid", intl.formatMessage({
				id: "LABEL_MAX_BID",
				defaultMessage: "Max Bid",
			})],
			["strategy_min_bid", intl.formatMessage({
				id: "LABEL_MIN_BID",
				defaultMessage: "Min Bid",
			})],
		];

		// Filter out strategy_pacing_ratio if campaign type is PG
		const filteredColumns = campaign_type === "Programmatic Guaranteed"
			? allColumns.filter(([key]) => key !== "strategy_pacing_ratio")
			: allColumns;

		return new Map(filteredColumns);
	};

	const offlineAttributionColumns = {
		total_spend_roi: true,
		online_conversions: true,
		online_revenue: true,
		online_roi: true,
		offline_conversions: true,
		offline_revenue: true,
		offline_roi: true,
	};
	
	const performanceColumns = (whitelabelOfflineAttributionFlag = false) => {
    return {
      id: true,
      title: true,
      status: true,
      start_date: true,
      end_date: true,
      impressions: true,
      clicks: true,
      total_conversions: true,
      total_revenue: true,
      spend_to_date: true,
      total_spend_cpa: false,
      total_spend_cpc: false,
      total_spend_cpm: false,
      ctr: false,
      video_complete: false,
      ...(whitelabelOfflineAttributionFlag ? offlineAttributionColumns : {}),
    };
  };

	const spendColumns = {
		id: true,
		title: true,
		status: true,
		start_date: true,
		end_date: true,
		strategy_pacing_type: true,
		total_spend_yesterday: true,
		strategy_pacing_ratio: true,
		strategy_min_bid: true,
		strategy_max_bid: true,
	}

// set storage for campaign columns information
if (!storage.getItem('strategies_performance_grid')) {
	storage.setItem('strategies_performance_grid', performanceColumns(false));
}
if (!storage.getItem('strategies_spend_grid')) {
	storage.setItem('strategies_spend_grid', spendColumns);
}

/**
 * Render strategies grid
 * @return {JSX.Element}
 * @constructor
 */
export const StrategiesGrid = ({ items, loading, controls, selectedGridData, campaign_type }) => {
	const intl = useIntl();
	const context = React.useContext(StrategyGridContext);

	/**
	 * check should we show a cell based on flag in the map
	 * @param cellName
	 * @return {string}
	 */
	const cellVisible = (cellName) => {
		return selectedGridData.hasOwnProperty(cellName) && selectedGridData[cellName]
			? ""
			: "invisible";
	};

	/**
	 * click on the button to hide a popup
	 */
	const listener = () => {
		document.getElementById("edit-column-button").click();
	};

	/**
	 * attach a listener for mouse leave on edit-column popup
	 */
	const attachListener = () => {
		document
			.getElementById("edit-column-id")
			.addEventListener("mouseleave", listener);
	};

	/**
	 * remove a listener for mouse leave on component unmount
	 */
	const removeListener = () => {
		document
			.getElementById("edit-column-id")
			.removeEventListener("mouseleave", listener);
	};

  const onPageChange = (e, { activePage }) => {
    context.getPage(e, { activePage });
  };

	const {
		strategyForDuplication,
		setStrategyForDuplication,
		duplicateStrategy,
    selectedStrategies,
    setSelectedStrategies,
    setIsBulkEditModalOpen,
    isBulkEditModalOpen,
    dispatch,
    setBulkEditResponseMessage,
		onHeaderSort,
		sortBy,
		sortDir,
		setPerformanceGridData,
		setSpendGridData,
	} = context;

  const areAllStrategiesChecked = selectedStrategies.length === items.length;

  const handleCheckboxChange = useCallback((e, { checked }) => {
    setSelectedStrategies(checked ? items.map(({ id }) => id) : []);
  }, [items, setSelectedStrategies]);
	const isPerformanceView = controls.filter.isPerformanceView();

	return (
		<>
			<Modal
				open={strategyForDuplication}
				onClose={() => setStrategyForDuplication(null)}
				closeOnDimmerClick={false}
				closeOnDocumentClick={false}
			>
				<Modal.Header>
					{intl.formatMessage({
						id: "DUPLICATION_CONFIRMATION_HEADER",
						defaultMessage: "Confirm duplication",
					})}
				</Modal.Header>
				<Modal.Content>
					{strategyForDuplication &&
						intl.formatMessage(
							{
								id: "DUPLICATION_STRATEGY_CONFIRMATION_MSG",
								defaultMessage:
									"Are you sure you want to duplicate strategy - {title}?",
							},
							{ title: strategyForDuplication["title"] }
						)}
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={() => setStrategyForDuplication(null)}>
						{intl.formatMessage({
							id: "BTN_CANCEL",
							defaultMessage: "Cancel",
						})}
					</Button>
					<Button
						primary
						onClick={() => {
							duplicateStrategy({ ...strategyForDuplication });
							setStrategyForDuplication(null);
						}}
					>
						{intl.formatMessage({
							id: "BTN_OK",
							defaultMessage: "Ok",
						})}
					</Button>
				</Modal.Actions>
			</Modal>
			<Grid className="common_grid" doubling stackable columns={2}>
					<Grid.Column width={16}>
						<FilterControl
							filter={controls.filter}
							onChange={context.filtering}
						/>
					</Grid.Column>
					<Grid.Column
						width={16}
						className=""
						textAlign="right"
						verticalAlign="middle"
					>
						<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: "8px", rowGap: "8px" }}>
							<Popup
								trigger={
									<Button
										className="text__uppercase"
										id="edit-column-button"
										compact
									>
										{intl.formatMessage({
											id: "BTN_EDIT_COLUMNS",
											defaultMessage: "Edit Columns",
										})}
									</Button>
								}
								onMount={attachListener}
								onUnmount={removeListener}
								id="edit-column-id"
								content={
									<List>
										{Object.keys(selectedGridData).map((column) => {
											return (
												<List.Item key={uuid()}>
													<Checkbox
														label={columnNames(intl, campaign_type).get(column)}
														name={column}
														defaultChecked={selectedGridData[column]}
														onChange={(e, { name, checked }) => {
															(isPerformanceView ? setPerformanceGridData : setSpendGridData)({ ...selectedGridData, [name]: checked });
														}}
													/>
												</List.Item>
											);
										})}
									</List>
								}
								on="click"
								position="bottom right"
							/>
              <Popup
                content={
                  intl.formatMessage({
                    id: "TOOLTIP_SELECT_STRATEGIES_TO_BULK_EDIT",
                    defaultMessage: "Select one or more Strategies to Bulk Edit"
                  })
                }
                size="tiny"
                inverted
                position="top center"
                disabled={selectedStrategies.length > 0}
                trigger={
                  <div style={{ display: 'inline-block' }}>
                    <Button
                      className="text__uppercase"
                      primary
                      compact
                      onClick={() => setIsBulkEditModalOpen(true)}
                      disabled={selectedStrategies.length <= 0}
                    >
                      {intl.formatMessage({
                        id: "BTN_EDIT_STRATEGIES",
                        defaultMessage: "Bulk Edit Strategies",
                      })}
                    </Button>
                  </div>
                }
              />
							<Button
								className="text__uppercase"
								primary
								compact
								onClick={() => context.navigateToCreatePage()}
							>
								{intl.formatMessage({
									id: "BTN_ADD_STRATEGY",
									defaultMessage: "Add Strategy",
								})}
							</Button>
						</div>
					</Grid.Column>
			</Grid>
			<div className="strategy-grid-table-container">
			<Table className="custom-table" sortable>
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell style={{width:"40px"}}>
              <Checkbox
                checked={areAllStrategiesChecked}
                onChange={handleCheckboxChange}
              />
						</Table.HeaderCell>
						{Object.keys(selectedGridData).map((column) => {
								return (
									<Table.HeaderCell
										key={column}
										textAlign="left"
										className={`${cellVisible(column)} ${column === "title" ? "sticky-title": ""}`}
										onClick={() => onHeaderSort(column)}
										sorted={sortBy === column ? (sortDir === 'asc' ? 'ascending' : 'descending') : null}
										style={{ cursor: 'pointer' }}
									>
										{columnNames(intl, campaign_type).get(column)}
										{sortBy === column ? null : <Icon name="sort" size="small" />}
									</Table.HeaderCell>
								)})}
						<Table.HeaderCell textAlign="center">&nbsp;</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{items.map((item) => {
						return (
							<StrategyGridItem
								key={item.id}
								item={item}
								cellVisible={cellVisible}
								selectedGridData={selectedGridData}
							/>
						);
					})}
					<GridEmptyRow
						length={items.length}
						loading={loading}
						handleAddButton={context.navigateToCreatePage}
						filterTouched={!controls.filter.isEmpty()}
						addButtonLabel={intl.formatMessage({
							id: "BTN_ADD_STRATEGY",
							defaultMessage: "Add Strategy",
						})}
						notFoundMessage={intl.formatMessage({
							id: "EMPTY_STRATEGIES",
							defaultMessage: "You don’t have any strategies yet",
						})}
					/>
				</Table.Body>
				<Table.Footer>
					<Table.Row>
						<Table.Cell colSpan="11" textAlign="right">
							{controls.pager.total_pages > 1 && (
								<Pagination
									size="mini"
									activePage={controls.pager.page}
									totalPages={controls.pager.total_pages}
									firstItem={null}
									lastItem={null}
									onPageChange={onPageChange}
								/>
							)}
						</Table.Cell>
					</Table.Row>
				</Table.Footer>
			</Table>
			</div>
      <StrategiesBulkEditModal
        isBulkEditModalOpen={isBulkEditModalOpen}
        setIsBulkEditModalOpen={setIsBulkEditModalOpen}
        selectedStrategies={selectedStrategies}
        items={items}
        dispatch={dispatch}
        setBulkEditResponseMessage={setBulkEditResponseMessage}
      />
		</>
	);
};
